'use client'

import { useState, useEffect, useCallback } from 'react'
import { mockBGGGames, MockBGGGame } from '@/data/mock-bgg-games'
import { Game } from '@/services/events'
import { searchGames } from '@/services/events'
import { BGGGameDetails } from '@/services/bgg-api'
import useBGGGameSearch from '@/hooks/useBGGGameSearch'

// Simple utility function for class name merging
function cn(...inputs: (string | undefined | null | false)[]) {
  return inputs.filter(Boolean).join(' ')
}

type GameSource = 'library' | 'bgg' | 'mock'

// Create a union type to handle both Game and MockBGGGame
type GameItem = Game | MockBGGGame | BGGGameDetails

// Type guard to check if an item is a MockBGGGame
function isMockBGGGame(game: any): game is MockBGGGame {
  return game && typeof game === 'object' && 'thumbnail_url' in game && 'bgg_rank' in game
}

// Type guard to check if an item is a BGGGameDetails
function isBGGGameDetails(game: any): game is BGGGameDetails {
  return game && typeof game === 'object' && 'id' in game && 'thumbnail' in game
}

interface GameSelectProps {
  onGameSelect: (game: GameItem | null) => void
  selectedGame?: GameItem | null
  className?: string
}

function GameSelect({ onGameSelect, selectedGame, className }: GameSelectProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<Game[]>([])
  const [gameSource, setGameSource] = useState<GameSource>('bgg')
  const [mockResults, setMockResults] = useState<MockBGGGame[]>([])
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false)
  const [libraryError, setLibraryError] = useState<string | null>(null)

  // Use the BGG search hook
  const {
    results: bggResults,
    isLoading: isLoadingBGG,
    error: bggError,
    search: searchBGGOriginal,
    reset: resetBGGOriginal,
  } = useBGGGameSearch()

  // Memoize the functions to prevent them from causing infinite renders
  const searchBGG = useCallback(
    (query: string) => {
      searchBGGOriginal(query)
    },
    [searchBGGOriginal]
  )

  const resetBGG = useCallback(() => {
    resetBGGOriginal()
  }, [resetBGGOriginal])

  // Handle game search based on selected source
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([])
      setMockResults([])
      resetBGG()
      setLibraryError(null)
      return
    }

    if (gameSource === 'mock') {
      // Filter mock BGG games
      const filteredGames = mockBGGGames.filter(game =>
        game.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
      setMockResults(filteredGames)
      return
    }

    if (gameSource === 'library') {
      // Search user's library
      setIsLoadingLibrary(true)
      setLibraryError(null)
      searchGames(searchQuery)
        .then(({ data, error }) => {
          if (error) throw new Error(error)
          setSearchResults(data || [])
        })
        .catch(err => {
          console.error('Error searching games:', err)
          setSearchResults([])
          setLibraryError('Failed to search library games')
        })
        .finally(() => {
          setIsLoadingLibrary(false)
        })
    }

    if (gameSource === 'bgg') {
      // Use the BGG search hook
      searchBGG(searchQuery)
    }
  }, [searchQuery, gameSource, searchBGG, resetBGG])

  // Handle game selection
  const handleGameSelect = (game: GameItem) => {
    onGameSelect(game)
    setSearchQuery('')
    setSearchResults([])
    setMockResults([])
    resetBGG()
  }

  // Convert BGG game details to database format
  const convertBGGGameToGame = (bggGame: BGGGameDetails): Game => {
    // Normalize the image and thumbnail URLs
    const normalizeUrl = (url?: string): string => {
      if (!url) return ''
      if (url.startsWith('http:')) {
        return url.replace('http:', 'https:')
      }
      if (!url.startsWith('http') && !url.startsWith('/')) {
        return `https:${url}`
      }
      return url
    }

    // Get the most appropriate image URL (prefer image over thumbnail)
    const imageUrl = normalizeUrl(bggGame.image || bggGame.thumbnail)
    const thumbnailUrl = normalizeUrl(bggGame.thumbnail)

    console.log('Converting BGG game to Game:', {
      name: bggGame.name,
      originalImage: bggGame.image,
      originalThumbnail: bggGame.thumbnail,
      convertedImageUrl: imageUrl,
      convertedThumbnailUrl: thumbnailUrl,
    })

    // Store the thumbnail URL in the Game object directly as an object property
    // even though it's not in the DB schema (it will be used by the UI but won't be saved to DB)
    const game: Game = {
      id: `bgg-${bggGame.id}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      name: bggGame.name,
      description: bggGame.description || null,
      min_players: bggGame.minPlayers || 1,
      max_players: bggGame.maxPlayers || 1,
      min_playtime: bggGame.minPlaytime || 0,
      max_playtime: bggGame.maxPlaytime || 0,
      image_url: imageUrl,
      bgg_id: parseInt(bggGame.id),
      complexity: bggGame.complexity || 0,
      year_published: bggGame.yearPublished || 0,
    }

    // Add the thumbnail_url property to the game object (at runtime only)
    Object.defineProperty(game, 'thumbnail_url', {
      value: thumbnailUrl,
      enumerable: true, // Make it visible in console.log
      writable: true,
      configurable: true,
    })

    return game
  }

  // Convert mock game to database format if needed
  const convertMockGameToGame = (mockGame: MockBGGGame): Game => {
    return {
      id: mockGame.id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      name: mockGame.name,
      description: null,
      min_players: mockGame.min_players,
      max_players: mockGame.max_players,
      min_playtime: mockGame.min_playtime,
      max_playtime: mockGame.max_playtime,
      image_url: mockGame.image_url,
      bgg_id: parseInt(mockGame.id.replace('bgg-', '')),
      complexity: mockGame.complexity,
      year_published: mockGame.year_published,
    }
  }

  // Get the image URL based on game type
  const getGameImageUrl = (game: GameItem | null): string => {
    if (!game) return '/placeholder-game.png'

    let imageUrl = '/placeholder-game.png'

    if (isBGGGameDetails(game)) {
      imageUrl = game.thumbnail || game.image || '/placeholder-game.png'
    } else if (isMockBGGGame(game)) {
      imageUrl = game.thumbnail_url || '/placeholder-game.png'
    } else {
      imageUrl = game.image_url || '/placeholder-game.png'
    }

    // Make sure we have a valid URL
    if (imageUrl.startsWith('http:')) {
      imageUrl = imageUrl.replace('http:', 'https:')
    }

    // Make sure URL is absolute
    if (!imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
      imageUrl = `https:${imageUrl}`
    }

    return imageUrl
  }

  // Get the playtime string based on game type
  const getPlaytimeString = (game: GameItem | null): string => {
    if (!game) return ''

    if (isBGGGameDetails(game)) {
      const min = game.minPlaytime || '?'
      const max = game.maxPlaytime || '?'
      return `${min}-${max} min`
    }

    const min = game.min_playtime || '?'
    const max = game.max_playtime || '?'

    return `${min}-${max} min`
  }

  // Get min/max players based on game type
  const getPlayersString = (game: GameItem | null): string => {
    if (!game) return ''

    if (isBGGGameDetails(game)) {
      return `${game.minPlayers || '?'}-${game.maxPlayers || '?'}`
    }

    return `${game.min_players || '?'}-${game.max_players || '?'}`
  }

  // Determine if we're loading based on the current source
  const isLoading =
    gameSource === 'library' ? isLoadingLibrary : gameSource === 'bgg' ? isLoadingBGG : false

  // Get the current error message based on the source
  const errorMessage =
    gameSource === 'library' ? libraryError : gameSource === 'bgg' ? bggError : null

  return (
    <div className={cn('w-full space-y-2 relative', className)}>
      {/* Source toggle */}
      <div className="flex justify-center mb-4 bg-gray-800 rounded-lg p-1">
        <button
          className={cn(
            'px-4 py-2 rounded-md text-sm font-medium transition-colors',
            gameSource === 'library' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
          )}
          onClick={() => setGameSource('library')}
        >
          My Library
        </button>
        <button
          className={cn(
            'px-4 py-2 rounded-md text-sm font-medium transition-colors',
            gameSource === 'bgg' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
          )}
          onClick={() => setGameSource('bgg')}
        >
          BGG
        </button>
        <button
          className={cn(
            'px-4 py-2 rounded-md text-sm font-medium transition-colors',
            gameSource === 'mock' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
          )}
          onClick={() => setGameSource('mock')}
        >
          Mock
        </button>
      </div>

      {/* Game search input */}
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={`Search for a game on ${
            gameSource === 'library'
              ? 'your library'
              : gameSource === 'bgg'
              ? 'BoardGameGeek'
              : 'mock data'
          }...`}
          className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {isLoading && (
          <div className="absolute right-3 top-2.5">
            <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
          </div>
        )}
      </div>

      {/* Error message */}
      {errorMessage && (
        <div className="p-2 bg-red-900/40 border border-red-800 rounded-md text-sm text-red-200">
          {errorMessage}
        </div>
      )}

      {/* Selected game display */}
      {selectedGame && (
        <div className="flex items-center p-2 bg-gray-800 border border-gray-700 rounded-md mt-2">
          <img
            src={getGameImageUrl(selectedGame)}
            alt={selectedGame.name}
            className="w-12 h-12 object-cover rounded-md mr-3"
            onError={e => {
              e.currentTarget.src = '/placeholder-game.png'
            }}
          />
          <div>
            <p className="font-medium">{selectedGame.name}</p>
            <p className="text-sm text-gray-400">
              {getPlayersString(selectedGame)} players •{getPlaytimeString(selectedGame)}
            </p>
          </div>
          <button
            onClick={() => onGameSelect(null)}
            className="ml-auto text-gray-400 hover:text-white"
          >
            ✕
          </button>
        </div>
      )}

      {/* Search results */}
      {(searchResults.length > 0 || mockResults.length > 0 || bggResults.length > 0) && (
        <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto">
          {gameSource === 'library' &&
            searchResults.map(game => (
              <div
                key={game.id}
                className="flex items-center p-2 hover:bg-gray-700 cursor-pointer"
                onClick={() => handleGameSelect(game)}
              >
                <img
                  src={game.image_url || '/placeholder-game.png'}
                  alt={game.name}
                  className="w-10 h-10 object-cover rounded-md mr-3"
                  onError={e => {
                    e.currentTarget.src = '/placeholder-game.png'
                  }}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{game.name}</p>
                  <p className="text-sm text-gray-400 truncate">
                    {game.min_players}-{game.max_players} players •{game.min_playtime}-
                    {game.max_playtime} min
                  </p>
                </div>
              </div>
            ))}

          {gameSource === 'bgg' &&
            bggResults.map(game => (
              <div
                key={game.id}
                className="flex items-center p-2 hover:bg-gray-700 cursor-pointer"
                onClick={() => handleGameSelect(convertBGGGameToGame(game))}
              >
                <img
                  src={game.thumbnail || game.image || '/placeholder-game.png'}
                  alt={game.name}
                  className="w-10 h-10 object-cover rounded-md mr-3"
                  onError={e => {
                    e.currentTarget.src = '/placeholder-game.png'
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center">
                    <p className="font-medium truncate">{game.name}</p>
                    <span className="ml-2 text-xs bg-gray-700 px-1.5 py-0.5 rounded whitespace-nowrap">
                      {game.yearPublished || 'N/A'}
                    </span>
                    {game.bggRank && (
                      <span className="ml-2 text-xs bg-yellow-800 text-yellow-200 px-1.5 py-0.5 rounded whitespace-nowrap">
                        #{game.bggRank}
                      </span>
                    )}
                  </div>
                  <div className="flex text-sm text-gray-400 space-x-2 flex-wrap">
                    <span className="whitespace-nowrap">
                      {game.minPlayers || '?'}-{game.maxPlayers || '?'} players
                    </span>
                    <span className="hidden sm:inline">•</span>
                    <span className="whitespace-nowrap">
                      {game.minPlaytime || '?'}-{game.maxPlaytime || '?'} min
                    </span>
                    {game.complexity && (
                      <>
                        <span className="hidden sm:inline">•</span>
                        <span className="flex items-center whitespace-nowrap">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3 mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 10V3L4 14h7v7l9-11h-7z"
                            />
                          </svg>
                          {game.complexity.toFixed(1)}
                        </span>
                      </>
                    )}
                    {game.recommendedAge && (
                      <>
                        <span className="hidden sm:inline">•</span>
                        <span className="whitespace-nowrap">{game.recommendedAge}+</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}

          {gameSource === 'mock' &&
            mockResults.map(game => (
              <div
                key={game.id}
                className="flex items-center p-2 hover:bg-gray-700 cursor-pointer"
                onClick={() => handleGameSelect(game)}
              >
                <img
                  src={game.thumbnail_url || '/placeholder-game.png'}
                  alt={game.name}
                  className="w-10 h-10 object-cover rounded-md mr-3"
                  onError={e => {
                    e.currentTarget.src = '/placeholder-game.png'
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center">
                    <p className="font-medium truncate">{game.name}</p>
                    <span className="ml-2 text-xs bg-gray-700 px-1.5 py-0.5 rounded whitespace-nowrap">
                      {game.year_published}
                    </span>
                    {game.bgg_rank && (
                      <span className="ml-2 text-xs bg-yellow-800 text-yellow-200 px-1.5 py-0.5 rounded whitespace-nowrap">
                        #{game.bgg_rank}
                      </span>
                    )}
                  </div>
                  <div className="flex text-sm text-gray-400 space-x-2 flex-wrap">
                    <span className="whitespace-nowrap">
                      {game.min_players}-{game.max_players} players
                    </span>
                    <span className="hidden sm:inline">•</span>
                    <span className="whitespace-nowrap">
                      {game.min_playtime}-{game.max_playtime} min
                    </span>
                    <span className="hidden sm:inline">•</span>
                    <span className="flex items-center whitespace-nowrap">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                      {game.complexity.toFixed(1)}
                    </span>
                    <span className="hidden sm:inline">•</span>
                    <span className="whitespace-nowrap">{game.recommended_age}+</span>
                  </div>
                </div>
              </div>
            ))}

          {searchQuery.length >= 2 &&
            searchResults.length === 0 &&
            mockResults.length === 0 &&
            bggResults.length === 0 &&
            !isLoading && (
              <div className="p-3 text-center text-gray-400">
                No games found. Try a different search term.
              </div>
            )}
        </div>
      )}
    </div>
  )
}

export { GameSelect }
